/**
 * Der `SammelsendungDrawer` als nachgeladener Chunk: er (samt dem, was nur er braucht) gehört nicht ins Eintrittsbündel
 * (Grenze und NEGATIVLISTE, Kategorie N, in `test/reference/bundle.test.ts`), erst das Öffnen einer Sammelsendung lädt ihn.
 * Verwendet von `App.tsx`; Muster ist `components/Drawer/DocsTab/Massenupload/MassenuploadDialogLazy.tsx`.
 *
 * Diese Hülle importiert den Drawer nur zur Laufzeit und seine Props nur als Typ (`import type` wird gelöscht): ein statischer
 * Import des Drawers zöge ihn wieder ins Eintrittsbündel.
 *
 * ================= Wenn der Chunk nicht lädt =================
 *
 * Ein alter Tab nach einem Deployment kennt `SammelsendungDrawer-<hash>.js` nicht mehr: `import()` scheitert, `React.lazy` wirft
 * beim Rendern. Ohne Grenze dazwischen fängt das erst die Wurzelgrenze (`FehlerGrenze` in `src/App.tsx`) und ersetzt die GANZE
 * Anwendung durch „Bitte die Seite neu laden“. `DrawerGrenze` meldet stattdessen eine Zeile im Fehlerstapel, ruft `onSchliessen`
 * (die App setzt `sammelsendungOffen` zurück und zeigt wieder das Board) und zeichnet nichts. `React.lazy` merkt sich die
 * Ablehnung: ein zweiter Versuch ohne Neuladen scheitert gleich wieder, die Meldung bleibt dann dieselbe.
 *
 * Die Meldung geht als fertiger Satz an `meldeFehler` (nicht als Kontext vor dem Satz des Übersetzers `fehlertext`): bei einem
 * Chunk-Ladefehler hängte der sonst „Die Anwendung wurde aktualisiert. Bitte die Seite neu laden (F5).“ an, der Rat stünde
 * doppelt da. Das Gleiche gilt für einen Absturz beim Rendern des geladenen Drawers: die Grenze fängt ihn mit.
 */
import { Component, lazy, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import type { SammelsendungDrawerProps } from "./SammelsendungDrawer.tsx";
import { EscapeSperre } from "./TrennenDialogLazy.tsx";

const SammelsendungDrawerChunk = lazy(() =>
  import("./SammelsendungDrawer.tsx").then((m) => ({ default: m.SammelsendungDrawer })),
);

const NICHT_GEOEFFNET = "Die Sammelsendung konnte nicht geöffnet werden. Bitte die Seite neu laden.";

interface GrenzeEigenschaften {
  onSchliessen: () => void;
  children: ReactNode;
}

class DrawerGrenze extends Component<GrenzeEigenschaften, { abgestuerzt: boolean }> {
  state = { abgestuerzt: false };

  static getDerivedStateFromError(): { abgestuerzt: boolean } {
    return { abgestuerzt: true };
  }

  componentDidCatch(fehler: unknown, info: ErrorInfo): void {
    console.error("Sammelsendungs-Drawer abgestürzt:", fehler, info.componentStack);
    meldeFehler(NICHT_GEOEFFNET);
    this.props.onSchliessen();
  }

  render(): ReactNode {
    return this.state.abgestuerzt ? null : this.props.children;
  }
}

/**
 * Bis der Chunk da ist, verschluckt `EscapeSperre` die Escape-Taste: der Drawer hat seinen eigenen Hörer noch nicht, und ein
 * Escape in dieser Phase würde sonst die Board-Auswahl leeren.
 */
export function SammelsendungDrawerLazy(props: SammelsendungDrawerProps) {
  return (
    <DrawerGrenze onSchliessen={props.onSchliessen}>
      <Suspense fallback={<EscapeSperre />}>
        <SammelsendungDrawerChunk {...props} />
      </Suspense>
    </DrawerGrenze>
  );
}
